import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Boxes, Truck, Factory, Sparkles, ArrowRight } from "lucide-react";
import WorkLayout from "@/components/WorkLayout";
import { HUB, PAGES, LABELS } from "@/data/seoPages";

export default function WorkHub() {
  const jsonLd = {
    "@context": "https://schema.org", "@type": "WebSite", name: "WorkOnIT Jobs",
    url: (typeof window !== "undefined" ? window.location.origin : "") + "/work/jobs",
  };
  return (
    <WorkLayout
      title="Jobs in the Netherlands & Europe — Warehouse, Production, Logistics | WorkOnIT"
      description="Find warehouse, production, logistics, driver, cleaning, part-time and full-time jobs across the Netherlands and Europe. Live openings, top cities & salaries. Apply on WorkOnIT."
      canonicalPath="/work/jobs" image={HUB.hero} jsonLd={jsonLd}>

      <section className="relative">
        <div className="absolute inset-0"><img src={HUB.hero} alt="Warehouse in the Netherlands" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/40" /></div>
        <div className="relative max-w-6xl mx-auto px-5 py-24 sm:py-32">
          <span className="inline-flex items-center gap-2 text-xs font-mono tracking-wider uppercase text-teal-300 bg-teal-500/10 border border-teal-500/25 rounded-full px-3 py-1"><Sparkles size={13} /> Work in Europe</span>
          <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl">Find work across the Netherlands &amp; Europe</h1>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl">Warehouse, production, logistics, driving and cleaning jobs — with real salaries and direct applications. Many employers help with housing and transport for international candidates.</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/work/jobs/netherlands" data-testid="hub-nl-cta" className="px-6 py-3 rounded-xl bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold inline-flex items-center gap-2">Jobs in the Netherlands <ArrowRight size={17} /></Link>
            <Link to="/work/live-in-netherlands" className="px-6 py-3 rounded-xl border border-white/15 bg-white/5 text-slate-100 font-medium hover:bg-white/10">Live &amp; work in NL →</Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 py-14">
        <h2 className="font-display font-bold text-2xl mb-6 flex items-center gap-2"><MapPin size={20} className="text-teal-400" /> Popular job cities</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {HUB.cities.map((s) => {
            const p = PAGES[s];
            return (
              <Link key={s} to={`/work/jobs/${s}`} data-testid={`hub-city-${s}`} className="group relative rounded-2xl overflow-hidden border border-white/10 h-40">
                <img src={p.hero} alt={p.h1} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 to-transparent" />
                <div className="absolute bottom-0 p-4"><div className="font-display font-bold text-lg">{LABELS[s]}</div><div className="text-xs text-teal-300">View jobs →</div></div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 pb-16">
        <h2 className="font-display font-bold text-2xl mb-6 flex items-center gap-2"><Boxes size={20} className="text-teal-400" /> Browse by sector</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {HUB.categories.map((s) => {
            const p = PAGES[s];
            const Icon = s.includes("driver") ? Truck : s.includes("production") ? Factory : Boxes;
            return (
              <Link key={s} to={`/work/jobs/${s}`} data-testid={`hub-cat-${s}`} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-slate-900/40 p-4 hover:border-teal-500/40 hover:bg-slate-900/70 transition-colors">
                <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0"><img src={p.hero} alt="" className="w-full h-full object-cover" /></div>
                <div className="min-w-0"><div className="font-semibold flex items-center gap-2"><Icon size={15} className="text-teal-400" /> {LABELS[s]} jobs</div><div className="text-xs text-slate-400 truncate">{p.metaDescription}</div></div>
              </Link>
            );
          })}
        </div>
      </section>
    </WorkLayout>
  );
}
